/**
 * SDK exceptions.
 *
 * `AtbashAPIError` is thrown for any non-2xx HTTP response from the judge or
 * risk-engine API. It surfaces the raw body in `Error.message` and appends
 * dashboard-aware hints for the common operational failure modes.
 */
import { DEFAULT_ENDPOINT } from "./constants.js";

export class AtbashAPIError extends Error {
  /** HTTP status code (or 0 if the request never completed). */
  readonly status: number;
  /** Raw response body text (may be empty). */
  readonly body: string;

  constructor(
    status: number,
    body: string,
    statusText = "",
    endpoint: string = DEFAULT_ENDPOINT,
  ) {
    super(enrich(status, body, statusText, endpoint));
    this.name = "AtbashAPIError";
    this.status = status;
    this.body = body;
  }
}

export class SignatureVerificationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SignatureVerificationError";
  }
}

/** Append dashboard-aware hints for common operational failure modes. */
function enrich(
  status: number,
  body: string,
  statusText: string,
  endpoint: string,
): string {
  const dashboard = endpoint.replace(/\/+$/, "") || DEFAULT_ENDPOINT;
  let msg = `API error ${status}: ${body || statusText}`;
  const lowered = body.toLowerCase();
  if (lowered.includes("agent not registered")) {
    msg += `\n  → Onboard the agent at ${dashboard}/risk-engine/agents`;
  } else if (
    lowered.includes("agent has no policy") ||
    lowered.includes("no policy configured")
  ) {
    msg += `\n  → Attach a policy at ${dashboard}/risk-engine/agents`;
  } else if (
    lowered.includes("agent is jailed") ||
    lowered.includes("jailed")
  ) {
    msg += `\n  → Unjail the agent at ${dashboard}/risk-engine/agents`;
  } else if (
    lowered.includes("audit tier") ||
    lowered.includes("verdict disabled") ||
    lowered.includes("verdict not supported")
  ) {
    msg += `\n  → Upgrade the org tier at ${dashboard}/risk-engine/settings`;
  } else if (status >= 400 && status < 500) {
    msg += `\n  → Dashboard: ${dashboard}/risk-engine/feed`;
  }
  return msg;
}
